import { css } from "@emotion/react";
import type { RowClickedEvent } from "ag-grid-community";
import { useMemo, useState } from "react";
import type { OrganisationWithPermissions } from "../../../../../api/src/types";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton } from "../../bits/buttons";
import { AdminTable } from "../../bits/inputs/tables/AdminTable";
import { orgTableColDef } from "../../bits/inputs/tables/orgColDefs";
import { FlexRowContainer } from "../../bits/layout";
import { LoadingSpinner } from "../../bits/loading";
import { Modal } from "../../bits/Modal";
import { useTitle } from "../../hooks/useTitle";
import type { OrganisationFormData } from "../../patterns/AddEditOrgForm";
import { AddOrganisationForm } from "../../patterns/AddEditOrgForm";

export const OrganisationAdmin = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { data: publications } = trpc.adminGetPublications.useQuery();
  const [editingOrganisation, setEditingOrganisation] =
    useState<OrganisationWithPermissions>();
  const { data } = trpc.adminGetOrganisations.useQuery();
  const createOrganisation = trpc.adminCreateOrganisation.useMutation({
    onSuccess: () => setIsModalOpen(false),
  });
  const updateOrganisation = trpc.adminUpdateOrganisation.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingOrganisation(undefined);
    },
  });
  const deleteOrganisation = trpc.adminDeleteOrganisation.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setIsModalOpen(false);
      setEditingOrganisation(undefined);
    },
  });
  useTitle("Organisation Admin | Class Legal eReader");

  const handleSubmit = (organisation: OrganisationFormData) => {
    if (editingOrganisation) {
      updateOrganisation.mutate({
        ...editingOrganisation,
        permissions: editingOrganisation.permissions?.map((p) => p.id),
        ...organisation,
        expiry: organisation.expiry.toISOString(),
      });
    } else {
      createOrganisation.mutate({
        ...organisation,
        expiry: organisation.expiry.toISOString(),
      });
    }
  };

  const handleSelectRow = (event: RowClickedEvent) => {
    setEditingOrganisation(event.data);
    setIsModalOpen(true);
  };

  const organisationDataWithPermissions = useMemo(() => {
    if (!data) return [];

    return data.map((organisation) => {
      const permissions = organisation.permissions.reduce((acc, pub) => {
        return {
          ...acc,
          ["publication-" + pub.publicationId]: true,
        };
      }, {});
      return {
        ...organisation,
        ...permissions,
      };
    });
  }, [data]);

  const organisationTableColDefWithPublications = useMemo(() => {
    const colDef = orgTableColDef(publications);
    return colDef;
  }, [publications]);

  return (
    <div style={{ padding: "1rem" }}>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          width: 100%;
        `}
      >
        <Modal
          key="modal"
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingOrganisation(undefined);
            createOrganisation.reset();
            updateOrganisation.reset();
            deleteOrganisation.reset();
          }}
          renderButton={(props) => (
            <FlexRowContainer style={{ justifyContent: "end" }}>
              <PrimaryButton
                {...props}
                onClick={() => setIsModalOpen(true)}
                disabled={createOrganisation.isLoading}
                style={{ marginBottom: "1rem" }}
              >
                Add new organisation
              </PrimaryButton>
            </FlexRowContainer>
          )}
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
                width: "100%",
              }}
            >
              <h2>
                {editingOrganisation
                  ? "Update organisation"
                  : "Add new organisation"}
              </h2>
              {editingOrganisation && (
                <Modal
                  key="deleteModal"
                  isOpen={isDeleteModalOpen}
                  onClose={() => {
                    setIsDeleteModalOpen(false);
                  }}
                  renderButton={() => (
                    <PrimaryButton
                      onClick={() => setIsDeleteModalOpen(true)}
                      disabled={
                        !!editingOrganisation.users?.length ||
                        deleteOrganisation.isLoading
                      }
                      style={{ marginBottom: "1rem" }}
                    >
                      Delete Organisation
                    </PrimaryButton>
                  )}
                  header={<h2>Delete Organisation</h2>}
                  content={
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                      `}
                    >
                      {`Are you sure you want to delete ${editingOrganisation.name}?`}
                    </div>
                  }
                  footer={
                    <PrimaryButton
                      onClick={() => {
                        deleteOrganisation.mutate(editingOrganisation);
                      }}
                    >
                      Delete
                    </PrimaryButton>
                  }
                />
              )}
            </div>
          }
          content={
            <AddOrganisationForm
              onSubmit={handleSubmit}
              error={
                createOrganisation.error?.message ??
                updateOrganisation.error?.message ??
                deleteOrganisation.error?.message ??
                ""
              }
              organisation={editingOrganisation}
            />
          }
          footer={<></>}
        />
      </div>
      {data ? (
        <AdminTable
          rowData={organisationDataWithPermissions}
          columnDefs={organisationTableColDefWithPublications}
          onRowClicked={handleSelectRow}
        />
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};
